import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Calendar, Clock, User, Tag, ArrowLeft, ArrowRight, Share2, Facebook, Twitter } from "lucide-react";
import { BLOG_POSTS } from "../mock/mock";

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" />;

  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <>
      <Header />
      <main>
        <section className="relative pt-40 pb-20 overflow-hidden">
          <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-900/60 to-slate-950/80" />
          <div className="relative max-w-4xl mx-auto px-6 text-white">
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-xs font-semibold tracking-wider uppercase">
              <Tag className="w-3 h-3" /> {post.category}
            </div>
            <h1 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight leading-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {post.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-white/85">
              <span className="flex items-center gap-1"><User className="w-4 h-4" /> {post.author}</span>
              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {post.date}</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {post.readTime}</span>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <article className="max-w-3xl mx-auto px-6">
            <p className="text-lg text-slate-700 leading-relaxed font-medium border-l-4 border-teal-600 pl-5 italic">
              {post.excerpt}
            </p>

            <div className="mt-8 space-y-6 text-slate-800 leading-relaxed text-lg">
              <p>
                Travel isn't just about the destination — it's about the stories you gather along the way, the strangers who become friends over a cup of masala chai, and the tiny moments of wonder that stay with you long after the journey ends.
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Why this destination made the list
              </h2>
              <p>
                India offers a stunning variety of experiences packed into a single subcontinent. Whether you're chasing the peace of an early-morning temple bell, the buzz of a night bazaar, or the salt-spray of a coastal drive, every corner reveals something you didn't know you were looking for.
              </p>
              <p>
                In our years of curating tours for the PMR family, one thing has become certain: the best trips are the simple ones. Fewer stops, more meaningful moments. Better hosts, better food, longer conversations.
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Practical tips before you go
              </h2>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>Book at least 3-4 weeks ahead during peak season for the best fares.</li>
                <li>Pack a rain-cover for your bag if travelling June-September.</li>
                <li>Carry a small pouch with basic medication, sunscreen, and a power bank.</li>
                <li>Learn 5-10 phrases in the local language — it opens more doors than money.</li>
                <li>Always tip local guides and drivers. They make your journey possible.</li>
              </ul>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Final thoughts
              </h2>
              <p>
                Every trip is a story waiting to be lived. Whether you follow our itinerary to the letter or wander off into your own adventure, we hope this guide helps you travel a little smarter, a little kinder, and with a little more joy in your step.
              </p>
              <p>
                Ready to plan your own escape? Give us a call at <a href="tel:+919397363140" className="text-teal-700 font-semibold underline">+91 9397363140</a> or drop us a line via the Contact page.
              </p>
            </div>

            <div className="mt-10 pt-6 border-t border-slate-100 flex items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">#{t}</span>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Share:</span>
                <a href="#" className="w-8 h-8 rounded-full bg-slate-100 hover:bg-teal-600 hover:text-white flex items-center justify-center transition">
                  <Facebook className="w-3.5 h-3.5" />
                </a>
                <a href="#" className="w-8 h-8 rounded-full bg-slate-100 hover:bg-teal-600 hover:text-white flex items-center justify-center transition">
                  <Twitter className="w-3.5 h-3.5" />
                </a>
                <button className="w-8 h-8 rounded-full bg-slate-100 hover:bg-teal-600 hover:text-white flex items-center justify-center transition">
                  <Share2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </article>

          <div className="max-w-7xl mx-auto px-6 mt-20">
            <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>You may also like</h3>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl hover:-translate-y-0.5 transition"
                >
                  <div className="h-44 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition duration-500" />
                  </div>
                  <div className="p-5">
                    <div className="text-[11px] font-bold text-teal-700 uppercase tracking-wider">{p.category}</div>
                    <h4 className="mt-1 font-bold text-slate-900 leading-snug group-hover:text-teal-700">{p.title}</h4>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500">
                      Read <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default BlogPostPage;
